import React, { useMemo } from "react";
import { Box, Paper, Stack, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography } from "@mui/material";

const getString = (value, fallback = "-") => {
	if (value === null || value === undefined) return fallback;
	if (typeof value === "string" && value.trim() === "") return fallback;
	return value;
};

const toNumber = (value) => {
	if (value === null || value === undefined) return NaN;
	if (typeof value === "number") return value;
	const parsed = Number(String(value).replace(/[%\s,]/g, ""));
	return Number.isFinite(parsed) ? parsed : NaN;
};

const formatNumber = (value) => (Number.isFinite(value) ? value.toLocaleString() : "-");
const formatPercent = (value) => (Number.isFinite(value) ? `${value.toFixed(0)}%` : "-");

const buildRowsFromBarcodes = (barcodes, nodBarcodesLength) => {
	if (!Array.isArray(barcodes) || barcodes.length === 0) return [];
	if (typeof barcodes[0] === "string") {
		return [
			{
				category: "Barcodes",
				option: "Selected",
				count: barcodes.length,
				discountPct: NaN,
				exclusionCount: nodBarcodesLength,
			},
		];
	}

	return barcodes.map((row, index) => ({
		id: row.id ?? index,
		category: getString(row.category ?? row.Category, "Category"),
		option: getString(row.option ?? row.Option ?? row.option_name ?? row.optionName, "Option"),
		optionCount: toNumber(row.optionCount ?? row.option_count),
		count: toNumber(row.count ?? row.Count ?? row.qty ?? row.Qty ?? row.quantity),
		discountPct: toNumber(row.discountPct ?? row.discount_percent ?? row.discount ?? row["Discount(%)"]),
		exclusionCount: toNumber(row.exclusionCount ?? row.exclusion_count ?? row.exclusion ?? row.excludedCount),
	}));
};

function SelectedProductSummary({ data }) {
	let summary = {};
		summary.qtyAffected = data.reduce((sum, row) => sum + (row.count || 0), 0);
		summary.discount = data.reduce((sum, row) => sum + (row.discount || 0), 0) / data.length || 0;
		summary.exclusionCount = data.reduce((sum, row) => sum + (row.exclusionCount || 0), 0);
		summary.option = data.reduce((sum, row) => sum + (row.option || 0), 0);
		summary.category = data.reduce((sum, row) => sum + 1, 0);

	return (
		<Box display="flex" gap={2} flexWrap="wrap" width={"80%"} pl={2} flexDirection={"row"} height={"100%"}>
			<Box>
				<Paper
					variant="outlined"
					sx={{
					borderRadius: 2,
					height: "200px",
					overflow: "hidden",
					width: "500px",
					}}
				>
					<TableContainer sx={{ height: "100%" }}>
					<Table size="small" stickyHeader>
						<TableHead>
						<TableRow>
							<TableCell>Category</TableCell>
							<TableCell>Brand</TableCell>
							<TableCell>Option</TableCell>
							<TableCell align="right">Count</TableCell>
							<TableCell align="right">Discount%</TableCell>
							<TableCell align="right">Exclusion count</TableCell>
						</TableRow>
						</TableHead>

						<TableBody>
						{data.length ? (
							data.map((row) => (
							<TableRow key={row.id ?? `${row.category}-${row.option}`}>
								<TableCell>{row.category}</TableCell>
								<TableCell>{row.brand}</TableCell>
								<TableCell>{row.option}</TableCell>
								<TableCell align="right">
								{formatNumber(row.count)}
								</TableCell>
								<TableCell align="right">
								{formatPercent(row.discount)}
								</TableCell>
								<TableCell align="right">
								{formatNumber(row.exclusionCount)}
								</TableCell>
							</TableRow>
							))
						) : (
							<TableRow>
							<TableCell colSpan={5} align="center">
								<Typography variant="body2" color="text.secondary">
								No product summary available
								</Typography>
							</TableCell>
							</TableRow>
						)}
						</TableBody>
					</Table>
					</TableContainer>
				</Paper>
				</Box>
			<Box>
				<Paper variant="outlined" sx={{ p: 2, borderRadius: 2, height: "fit-content" }}>
					<Stack spacing={1}>
						<Typography variant="subtitle2" color="text.secondary">
							Summary
						</Typography>
						<Typography variant="body2">Category Count: {formatNumber(summary.category)}</Typography>
						<Typography variant="body2">Option Count: {formatNumber(summary.option)}</Typography>
						<Typography variant="body2">Qty affected: {formatNumber(summary.qtyAffected)}</Typography>
						<Typography variant="body2">Discount(%): {formatPercent(summary.discount)}</Typography>
						<Typography variant="body2">Exclusion Count: {formatNumber(summary.exclusionCount)}</Typography>
					</Stack>
				</Paper>
			</Box>
		</Box>
	);
}

export default SelectedProductSummary;
